import React, { ReactNode, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { VideoPlayer } from '../../components'
import { useLocation, useNavigate } from 'react-router-dom'
import { ModuleBackground, ReturnButton } from '../../components/molecules'
import StudentMenu from '../../components/organisms/Menu/StudentMenu'
import { getVideoUrl } from '../../utils/video'
import { Button, PageTitle, SkipLink, Text } from '../../components/atoms'
import apiServices from '../../libs/services'
import { useDispatch, useSelector } from 'react-redux'
import { modules } from '../../utils'
import { RootState } from '../../redux/store'
import { Module } from '../../models/global.types'
import { setModules } from '../../redux/slices/moduleSlice'
import { ControlHighlight, KeyboardControl } from '../../components/molecules/KeyboardControls'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { defaultPageTitle } from '../../components/atoms/PageTitle/PageTitle'
import { getModuleNameScreenReader, parseModuleName } from '../../utils/modules'
// PreferenceType import removed as unused
import { useUserPreferences } from '../../contexts/UserPreferencesContext'

const placeholderButtonVideoPart = <span className='btn-replay-video-part' />
export const idReplayPageMainContent = 'replay-page-main-content'

export const idButtonVideoPrev = 'button-video-replay-previous'
export const idButtonVideoNext = 'button-video-replay-next'

const ModuleReplay: React.FC = () => {
  const keyboardControls = useKeyboardControls()
  const userPreferences = useUserPreferences()

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const modulesSlice: Module[] = useSelector((state: RootState) => state.module.modules)

  const [module, setModule] = useState<Module | null>(null)
  const [modulePart, setModulePart] = useState<number>(1)
  const [pageTitle, setPageTitle] = useState<string>(defaultPageTitle)
  const [videoUrl, setVideoUrl] = useState('')
  const [captionsUrl, setCaptionsUrl] = useState('')
  const [videoPlaying, setVideoPlaying] = useState<boolean>(false)
  const [videoPlayerKey, setVideoPlayerKey] = useState<number>(0)

  const query = new URLSearchParams(location.search)
  const moduleName = modules.parseModuleName(query.get('module'))
  const a11yParam = query.get('a11y') === 'true'

  const fetchModules = useCallback(async () => {
    if (!Array.isArray(modulesSlice) || modulesSlice.length <= 0) {
      const res = await apiServices.fetchModules()
      const modulesArr = res?.data?.data?.modules
      const m = modulesArr.filter(
        (module: Module) => `${module.name}`.toLowerCase() !== 'lightning').sort((a: any, b: any) => a.sort - b.sort)
      dispatch(setModules(m))
    }
  }, [dispatch, modulesSlice])

  // Reload video with current preferences without restarting module
  const reloadVideo = useCallback(() => {
    if (module === null) return

    const wasPlaying = videoPlaying
    setVideoPlaying(false)

    // Force video player to remount by changing the key
    setVideoPlayerKey(prev => prev + 1)

    // Update video URLs based on EAD preference
    setTimeout(() => {
      const [newVideoUrl, newCaptionsUrl] = getVideoUrl(module.name, modulePart, userPreferences.preferenceA11yEad || a11yParam)
      setVideoUrl(newVideoUrl)
      setCaptionsUrl(newCaptionsUrl)

      // Resume playing if video was playing before reload
      if (wasPlaying) {
        setTimeout(() => {
          setVideoPlaying(true)
        }, 200)
      }
    }, 100)
  }, [module, modulePart, userPreferences.preferenceA11yEad, a11yParam, videoPlaying])

  /// Initial data fetching
  useEffect(() => {
    // Get modules if the store doesn't have them.
    // TODO: refactor with Redux flow.
    if (!Array.isArray(modulesSlice) || modulesSlice.length <= 0) {
      fetchModules()
    }

    const module = modulesSlice.find(m => m.name.toLowerCase() === moduleName)
    if (module === undefined) { return }

    setModule(module)
    setModulePart(1)
    if (module !== null) {
      const moduleName = parseModuleName(module.name)
      setPageTitle(`${getModuleNameScreenReader(moduleName)} Level - Replay Video Lesson`)
    }
  }, [fetchModules, moduleName, modulesSlice])

  /// Update current video
  useEffect(() => {
    if (module === undefined || module === null) { return }

    const [videoUrl, captionsUrl] = getVideoUrl(module.name, modulePart, userPreferences.preferenceA11yEad || a11yParam)
    setVideoUrl(videoUrl)
    setCaptionsUrl(captionsUrl)
  }, [module, userPreferences.preferenceA11yEad, a11yParam, modulePart])

  // Listen for EAD preference changes and reload video
  const previousEadPreference = useRef<boolean>(userPreferences.preferenceA11yEad)
  useEffect(() => {
    if (previousEadPreference.current !== userPreferences.preferenceA11yEad && module !== null) {
      reloadVideo()
    }
    previousEadPreference.current = userPreferences.preferenceA11yEad
  }, [userPreferences.preferenceA11yEad, module, reloadVideo])

  const onVideoEnded = (): void => {
    if (module === null) return

    // Check if current part is the last one
    if (modulePart < module.partCount) {
    // If not the last part, advance to the next video
      setModulePart(modulePart + 1)
    } else {
    // If it's the last part, navigate to dashboard
      navigate('/user-dashboard')
    }
  }

  const renderButtonReturn = useMemo((): ReactNode => {
    return (
      <ReturnButton
        id='return-button-module-replay'
        onClick={() => navigate('/user-dashboard')}
        text='Return to Dashboard'
      />
    )
  }, [navigate])

  const renderButtonVideo = useCallback((id: string, text: string, onClick: (() => void)): ReactNode => {
    return (
      <ControlHighlight
        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(id)}
        highlightEffect={HighlightEffect.ButtonHexOutline}
        data-testid={`highlight-${id}`}
      >
        <KeyboardControl>
          <Button
            id={id}
            data-testid={id}
            bgColor='bg-dopage-lightGreen'
            className='diamond-btn-sm btn-replay-video-part font-bold p-[3px]'
            tabIndex={-1}
            onClick={onClick}
          >
            <div className='bg-dopage-black flex justify-center items-center diamond-btn-sm'>
              <Text
                className='font-bold text-center text-[26px] text-dopage-lightGreen'
                bold
                size='SM'
                text={text}
              />
            </div>
          </Button>
        </KeyboardControl>
      </ControlHighlight>
    )
  }, [keyboardControls])

  const renderButtonVideoPrev = useMemo((): ReactNode => {
    if (module === null) { return placeholderButtonVideoPart }
    if (modulePart === 1) { return placeholderButtonVideoPart }

    return renderButtonVideo(idButtonVideoPrev, '< Previous', () => {
      let newPart = modulePart - 1
      if (newPart < 1) {
        newPart = 1
      }
      setModulePart(newPart)
    })
  }, [module, modulePart, renderButtonVideo])

  const renderButtonVideoNext = useMemo((): ReactNode => {
    if (module === null) { return placeholderButtonVideoPart }
    if (modulePart === module.partCount) { return placeholderButtonVideoPart }

    return renderButtonVideo(idButtonVideoNext, 'Next Video >', () => {
      let newPart = modulePart + 1
      if (newPart > module.partCount) {
        newPart = module.partCount
      }
      setModulePart(newPart)
    })
  }, [module, modulePart, renderButtonVideo])

  const handlePlay = useCallback((): void => {
    setVideoPlaying(true)
  }, [])

  const handlePause = useCallback((): void => {
    setVideoPlaying(false)
  }, [])

  if (module !== null) {
    return (
      <div>
        <SkipLink targetId={idReplayPageMainContent} />
        <ModuleBackground
          moduleName={moduleName}
          className='p-4 flex flex-col'
          data-testid='background-module-replay'
        >
          <header>
            <StudentMenu />
          </header>
          <main
            id={idReplayPageMainContent}
            tabIndex={-1}
          >
            <PageTitle title={pageTitle} />
            <div className='flex justify-center items-tops p-8'>
              <div className='h-[70vh] min-h-[200px]'>
                <VideoPlayer
                  key={videoPlayerKey}
                  handleEnded={onVideoEnded}
                  handlePlay={handlePlay}
                  handlePause={handlePause}
                  instructionMode={false}
                  pauseOnFocusLost={false}
                  isPlaying={videoPlaying}
                  url={videoUrl}
                  subTitlesUrl={captionsUrl}
                  secondaryLeftComponent={renderButtonReturn}
                  controlLeftComponent={renderButtonVideoPrev}
                  controlRightComponent={renderButtonVideoNext}
                />
              </div>
            </div>
          </main>
        </ModuleBackground>
        <footer className='sr-only' />
      </div>
    )
  } else {
    return null
  }
}

export default ModuleReplay
